import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import classNames from "classnames";
import GalleryList from "react-photo-gallery";

import { Box, Button, Center, chakra, Flex, Text, useBoolean } from "@chakra-ui/react";
import { genBlock } from "@/utils/bem";
import { WithClassName } from "@/types/base";

import { IAlbumImageInfo, IAlbumItem } from "@/modules/album/store";
import { albumStore } from "@/modules/album/store";

import Icon from "@/components/Icon";
import { ReactComponent as ArrowSvg } from "@/assets/icons/left_arrow.svg";
import { ReactComponent as EmptyAlbum } from "@/assets/icons/empty_album.svg";
import { ReactComponent as AddSvg } from "@/assets/icons/pure_add.svg";
import getScrollBarProps from "@/styles/scrollBarProps";
import "./style.scss";
import { CreatorAlbumImageItem, FormatImageDataType } from "../AlbumImageItem";
import GalleryDetail from "@/pages/Gallery/components/GalleryDetail";
import { onAlbum, onFollow } from "@/pages/Gallery/hooks";
import OperationMenu from "@/modules/album/components/OperationMenu";
import { CreateAlbumModal } from "@/modules/album/components/CreateModal";
import { DeleteAlbumImagesModal } from "@/modules/album/components/DeleteImagesModal";
import { BoardImageDetailModal } from "@/components/bussiness/BoardImageDetail";
import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { Editor_Album_Words, useTranslate } from "@/utils/lang";
import { AlbumWords } from "@/utils/lang/album";
import { useAlbumListMenuScroll } from "@/modules/album/hook/useAlbumListMenuScroll";

const block = genBlock("c-creator-album-detail");
const AlbumDetail: React.FC<
  WithClassName<{
    album: Pick<IAlbumItem, "id" | "name">;
  }>
> = ({ album, className }) => {
  const { translate } = useTranslate();
  const { detail, clearCurSelectAlbum } = albumStore;
  const { album_image: images = [], album_id } = detail ?? {};
  // 多选
  const [inSelectMode, setInSelectMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Set<number>>(new Set());
  // 详情
  const [imageDetailData, setImageDetailData] = useState<IAlbumImageInfo | null>(null);
  const [detailModalOpen, detailModalOp] = useBoolean(false);
  // 弹窗
  const [modalType, setModal] = useState("rename");
  const [modalVisible, modalOp] = useBoolean(false);
  // 加载更多
  const hasMore = useMemo(() => {
    return albumStore.detailPageIdx < albumStore.detailMaxPages;
  }, [albumStore.detailPageIdx, albumStore.detailMaxPages]);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollContainerRef = useBottomScrollListener(
    () => {
      if (!hasMore) return;
      if (loadingMore) return;
      setLoadingMore(true);
      albumStore.getAlbumDetail(album_id, true).finally(() => {
        setLoadingMore(false);
      });
    },
    {
      offset: 1,
      debounce: 800,
      triggerOnNoScroll: hasMore,
    },
  );

  useEffect(() => {
    albumStore.getAlbumDetail(album.id);
    return () => {
      albumStore.imagesManager.reset();
      albumStore.clearAlbumDetail();
    };
  }, [album.id]);

  function handleSelectModeChange() {
    if (!images.length) return;
    if (inSelectMode) {
      const set = new Set<number>();
      setSelectedImages(set);
      setInSelectMode(false);
    } else {
      setInSelectMode(true);
    }
  }

  function handleSelect(imageId: number) {
    setSelectedImages((selected) => {
      const newSet = new Set(selected);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  }

  function handleDetailClick(image: IAlbumImageInfo) {
    setImageDetailData(image);
    detailModalOp.on();
  }

  const handleMovePictures = (id: string) => {
    if (id === "create") {
      setModal("create");
      modalOp.on();
    } else {
      const fromId = album_id!,
        toId = +id,
        size = selectedImages.size;
      if (fromId === toId || size === 0) return;
      albumStore.movePictures(fromId, toId, Array.from(selectedImages)).then((res) => {
        if (res.code === 0) {
          clearMultiSelect();
        }
      });
    }
  };

  const handleShowDelete = () => {
    setModal("deleteImage");
    modalOp.on();
  };

  const clearMultiSelect = () => {
    setSelectedImages(new Set());
    setInSelectMode(false);
  };

  const modal = useMemo(() => {
    switch (modalType) {
      case "deleteImage":
        return (
          <DeleteAlbumImagesModal
            isOpen={modalVisible}
            onClose={modalOp.off}
            albumId={album_id!}
            imageIds={Array.from(selectedImages)}
            onDelete={() => {
              clearMultiSelect();
            }}
          />
        );
      case "create":
        return <CreateAlbumModal isOpen={modalVisible} onClose={modalOp.off} />;
    }
  }, [modalType, modalVisible, modalOp.off, album_id]);

  const scrollProps = useMemo(() => {
    return getScrollBarProps({ autoMargin: false });
  }, []);

  const formatPhotos = useMemo(
    () =>
      images?.map((item) => {
        return {
          imageData: item,
          src: item.image_url,
          width: item.width ?? 300,
          height: item.height ?? 300,
          key: item.id.toString(),
        };
      }),
    [images],
  );

  const { scrollContainerRef: albumListScrollRef } = useAlbumListMenuScroll();

  return (
    <Box className={classNames(className, block())} bg="bg.panelBg" w="100%" h="100%">
      <Flex
        className={block("header")}
        px="16px"
        pb="16px"
        w="100%"
        fontSize="xs"
        color="text.secondary"
        fontWeight={500}
        justifyContent="space-between"
        alignItems="center"
      >
        <Center onClick={() => clearCurSelectAlbum()} cursor="pointer">
          <Icon className={classNames(block({ e: "arrow" }))} svg={ArrowSvg} fontSize="20px" />
          {translate(Editor_Album_Words.goBack)}
        </Center>
        <Text fontSize="md" color="text.subtitle">
          {album.name}
        </Text>

        <Button
          variant="link"
          color="text.secondary"
          size="sm"
          disabled={!images.length}
          cursor="pointer"
          onClick={handleSelectModeChange}
        >
          {inSelectMode
            ? translate(Editor_Album_Words.exitMultiSelect)
            : translate(Editor_Album_Words.multiSelect)}
        </Button>
      </Flex>
      {images.length ? (
        <Box
          className={block({ e: "gallery-wrapper" })}
          ref={scrollContainerRef as any}
          {...scrollProps}
        >
          <GalleryList
            columns={2}
            direction="column"
            photos={formatPhotos}
            margin={4}
            renderImage={(data) => {
              const isChecked = selectedImages.has(
                (data as FormatImageDataType).photo.imageData.id,
              );
              return (
                <CreatorAlbumImageItem
                  key={data.photo.key}
                  data={data as FormatImageDataType}
                  onSelect={handleSelect}
                  selectable={inSelectMode}
                  selected={isChecked}
                  onShowDetail={handleDetailClick}
                />
              );
            }}
          />
        </Box>
      ) : (
        <Center w="100%" h="100%" flexDirection="column">
          <Icon fontSize="72px" svg={EmptyAlbum} />
          <Text color="text.subtitle" mt="24px">
            {translate(Editor_Album_Words.emptyAlbum)}
          </Text>
        </Center>
      )}
      {inSelectMode ? (
        <Center
          w="100%"
          h="72px"
          gap="8px"
          p="18px 22px"
          bg="bg.panelBg"
          boxShadow="0px -4px 8px rgba(0, 0, 0, 0.25)"
        >
          <OperationMenu
            menuList={albumStore.albumListOptions}
            onSelect={handleMovePictures}
            menuListRef={albumListScrollRef as any}
            staticList={[
              {
                key: "create",
                content: (
                  <Flex alignItems="center">
                    <Icon svg={AddSvg} squared={false} fillbyCurrentColor mr="4px" />
                    {translate(AlbumWords.newAlbum)}{" "}
                  </Flex>
                ),
              },
            ]}
            variant="creatorMoveImage"
            value={album_id?.toString() ?? ""}
            disabled={!selectedImages.size}
            styles={{
              divider: {
                mt: "6px",
              },
            }}
          >
            <Button w="134px" variant="outline" flex={1} disabled={!selectedImages.size}>
              {translate(AlbumWords.changeAlbum)}
            </Button>
          </OperationMenu>

          <Button
            variant="outline"
            flex={1}
            onClick={handleShowDelete}
            disabled={!selectedImages.size}
          >
            {translate(AlbumWords.deleteImage)}
          </Button>
        </Center>
      ) : null}
      {detailModalOpen && imageDetailData?.image_id && (
        <Center
          pos="fixed"
          zIndex={5}
          width="100vw"
          height="100vh"
          left={0}
          top={0}
          bg="rgba(0, 0, 0, 0.7)"
        >
          <GalleryDetail
            currentID={imageDetailData?.image_id}
            backgroundColor={"#27272A"}
            onClose={() => {
              detailModalOp.off();
              setImageDetailData(null);
            }}
            onAlbum={(type, albumData) => {
              onAlbum(type, albumData);
              albumStore.updateAlbumDetails();
            }}
            onFollow={onFollow}
          />
        </Center>
      )}
      {imageDetailData?.board_alias && (
        <BoardImageDetailModal
          open={detailModalOpen}
          onClose={() => {
            detailModalOp.off();
            setImageDetailData(null);
          }}
          alias={imageDetailData?.board_alias}
        />
      )}
      {modal}
    </Box>
  );
};

export const CreatorAlbumDetail = chakra(observer(AlbumDetail));
